import _ from 'lodash'
import MenuConfig from 'Menu'

/**
 * Monta o menu de navegação a partir do Menu.js, das páginas personalizadas do cliente, das
 * áreas/estações e dos perfis do usuário. É a mesma regra do menu antigo (perfis, ordem salva
 * pelo usuário, "Novo"/"Repaginado"/"Beta"), só que num formato único para as três telas:
 * barra lateral (computador), gaveta (tablet) e barra inferior + gaveta (celular).
 */

export const ALL_ROLES = [
  'workstation',
  'admin',
  'superuser',
  'planning',
  'realtimetracking',
  'manufacturing_eng',
  'quality',
  'logistic',
]

const allowed = (roles, itemRoles, outerRoles) => {
  const list = itemRoles || outerRoles
  if (!list) return true
  return roles.some((role) => list.includes(role))
}

/** Menu.js + páginas personalizadas agrupadas por "menuGrouper". */
const baseConfig = (customPages) => {
  const config = JSON.parse(JSON.stringify(MenuConfig))
  const grouped = _.groupBy(customPages || [], (page) => page.menuGrouper || 'Custom Pages')
  Object.keys(grouped).forEach((grouper) => {
    let group = config.find((item) => item.name === grouper)
    if (!group) {
      group = {
        name: grouper,
        path: 'custom',
        key: `custom:${grouper}`,
        icon: 'fas fa-layer-group',
        translate: '',
        subMenu: [],
        allowedRoles: ALL_ROLES,
        order: 100 + config.length,
      }
      config.push(group)
    }
    group.subMenu = group.subMenu || []
    grouped[grouper].forEach((page) => {
      group.subMenu.push({
        name: page.menuTitle,
        path: `/custom/${page.id}`,
        icon: 'fas fa-file-alt',
        translate: '',
        allowedRoles: page.roles && page.roles.length > 0 ? page.roles : ALL_ROLES,
      })
    })
  })
  return config
}

/**
 * Áreas com as estações que o usuário pode ver, na ordem que ele escolheu ("Organizar menu").
 * A ordem fica no areaStore (localStorage "<tenant>-areasMenu"), no mesmo formato do menu antigo.
 */
export const buildAreas = (areas, workStations, areasMenu) => {
  const byArea = _.groupBy(_.orderBy(workStations || [], ['shortName']), 'areaId')
  const saved = areasMenu || []
  return _.orderBy(areas || [], ['description'])
    .filter((area) => area.id !== -1 && byArea[area.id])
    .map((area, index) => {
      const savedArea = saved.find((item) => item.id === area.id)
      const stations = byArea[area.id].map((station, stationIndex) => {
        const savedStation =
          savedArea && (savedArea.subItems || []).find((item) => item.subPath === `${station.id}`)
        return {
          id: station.id,
          label: station.shortName,
          order: savedStation ? savedStation.order : 1000 + stationIndex,
        }
      })
      return {
        id: area.id,
        label: area.description,
        order: savedArea ? savedArea.order : 1000 + index,
        stations: _.sortBy(stations, 'order'),
      }
    })
    .sort((a, b) => a.order - b.order)
}

/** Formato que o areaStore guarda (compatível com o menu antigo). */
export const areasToStore = (areas) =>
  areas.map((area, index) => ({
    id: area.id,
    name: area.label,
    order: index + 1,
    subItems: area.stations.map((station, stationIndex) => ({
      subName: station.label,
      subPath: `${station.id}`,
      order: stationIndex + 1,
    })),
  }))

const badgesOf = (item) =>
  [item.isNew && 'new', item.isUpdated && 'updated', item.beta && 'beta'].filter(Boolean)

/**
 * Seções do menu já filtradas pelos perfis e ordenadas.
 * Cada seção: { key, label, icon, to?, items: [{ key, label, icon, to, hint, badges }], areas? }
 * `areas` aparece em Estações e Planejamento: cada área leva à visão geral da área e lista as
 * estações.
 */
export const buildSections = ({ t, roles, customPages, areas, savedOrder }) => {
  const sections = baseConfig(customPages)
    .filter((section) => !section.heading)
    .map((section, index) => {
      const key = section.key || section.path
      const label = section.translate ? t(section.translate) : section.name
      const items = (section.subMenu || [])
        .filter((item) => allowed(roles, item.allowedRoles, section.allowedRoles))
        .map((item) => ({
          key: `${key}/${item.path}`,
          label: item.translate ? t(item.translate) : item.name,
          icon: item.icon,
          to: item.path.startsWith('/') ? item.path : `/${section.path}/${item.path}`,
          hint: item.subLabel,
          badges: badgesOf(item),
        }))
      const hasChildren = (section.subMenu && section.subMenu.length > 0) || section.hasActivities
      // igual ao menu antigo: seção com submenu aparece se sobrar algum item permitido
      const visible = hasChildren ? items.length > 0 : allowed(roles, section.allowedRoles)
      if (!visible) return null
      const saved = (savedOrder || []).find((entry) => (entry.key || entry.path) === key)
      return {
        key,
        label,
        icon: section.icon,
        to: hasChildren ? undefined : `/${section.path}`,
        items,
        areas: section.hasActivities
          ? areas.map((area) => ({
              ...area,
              to: `/${section.path}/${section.subMenuPath}/${area.id}`,
              stations: area.stations.map((station) => ({
                ...station,
                to: `/${section.path}/${station.id}`,
              })),
            }))
          : undefined,
        order: saved ? saved.order : section.order !== undefined ? section.order : 50 + index,
      }
    })
    .filter(Boolean)
  return _.sortBy(sections, 'order')
}

/** Todos os destinos do menu numa lista só, para a busca. */
export const flatten = (sections, t) => {
  const list = []
  sections.forEach((section) => {
    if (section.to)
      list.push({
        key: section.key,
        label: section.label,
        path: '',
        to: section.to,
        icon: section.icon,
      })
    section.items.forEach((item) =>
      list.push({
        key: item.key,
        label: item.label,
        path: section.label,
        to: item.to,
        icon: item.icon || section.icon,
      })
    )
    ;(section.areas || []).forEach((area) => {
      list.push({
        key: `${section.key}/area/${area.id}`,
        label: area.label,
        path: `${section.label} · ${t('nav.areaOverview', 'visão geral da área')}`,
        to: area.to,
        icon: 'fas fa-th-large',
      })
      area.stations.forEach((station) =>
        list.push({
          key: `${section.key}/${station.id}`,
          label: station.label,
          path: `${section.label} · ${area.label}`,
          to: station.to,
          icon: section.icon,
        })
      )
    })
  })
  return list
}

const normalize = (text) =>
  String(text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()

/** Busca sem acento e por partes ("emb 2" acha "EMB2", "pareto ref" acha "Pareto de Refugo"). */
export const search = (entries, query) => {
  const words = normalize(query).split(/\s+/).filter(Boolean)
  if (words.length === 0) return []
  return entries
    .map((entry) => {
      const label = normalize(entry.label)
      const haystack = `${label} ${normalize(entry.path)}`
      const compact = haystack.replace(/\s+/g, '')
      if (!words.every((word) => haystack.includes(word) || compact.includes(word))) return null
      const starts = label.startsWith(words[0]) ? 0 : 1
      return { entry, score: starts * 10 + label.length / 100 }
    })
    .filter(Boolean)
    .sort((a, b) => a.score - b.score)
    .slice(0, 30)
    .map((result) => result.entry)
}

/** A rota atual pertence a este destino? (as rotas do app não diferenciam maiúsculas) */
export const isActivePath = (pathname, to, exact) => {
  const current =
    String(pathname || '/')
      .toLowerCase()
      .replace(/\/+$/, '') || '/'
  const target =
    String(to || '')
      .toLowerCase()
      .replace(/\/+$/, '') || '/'
  if (exact || target === '/') return current === target
  return current === target || current.startsWith(`${target}/`)
}
